import React, { type ReactNode } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import StatusBadge from './StatusBadge';
import { formatDateMMDDYYYY } from '../utils/formatDate';

/** One display-ready row of an encounter's claims table, as the claims list shows it. */
export interface ClaimTableRow {
  /** Item (`P-1203-1`) or claim (`P-1203-1-2`) number, shown verbatim. */
  number: string | null;
  providerName: string | null;
  claimType: string;
  status: string;
  createdAt: string;
}

const COLUMNS = [
  { label: 'CLAIM #', width: 100 },
  { label: 'PROVIDER', width: 160 },
  { label: 'CLAIM TYPE', width: 120 },
  { label: 'STATUS', width: 100 },
  { label: 'CREATED', width: 80 },
] as const;

interface AssociatedClaimsSectionProps {
  associatedClaims: ClaimTableRow[];
  childId: string | null;
  /**
   * Optional banner rendered below the table (e.g. the edit page's
   * shared-changes warning). Omit on read-only views. Mirrors the `warning`
   * prop convention used by the shared Patient / Encounter sections.
   */
  warning?: ReactNode;
  /** Whether the accordion starts expanded. Defaults to false. */
  defaultExpanded?: boolean;
}

/**
 * Displays an expandable panel showing all associated claims (siblings) for an
 * encounter. Used on the edit-claim page (which passes a shared-changes
 * `warning`) and the read-only encounter-details page (which does not).
 * Highlights the clicked child ticket when navigating from the claims list.
 */
const AssociatedClaimsSection = React.memo(function AssociatedClaimsSection({
  associatedClaims,
  childId,
  warning,
  defaultExpanded = false,
}: AssociatedClaimsSectionProps) {
  // Don't render if there are no associated claims
  if (!associatedClaims || associatedClaims.length === 0) {
    return null;
  }

  return (
    <Accordion
      disableGutters
      defaultExpanded={defaultExpanded}
      sx={{
        mt: 3,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
        backgroundColor: 'background.paper',
        '&::before': { display: 'none' },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="associated-claims-content"
        id="associated-claims-header"
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormatListBulletedIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
          <Typography sx={{ fontWeight: 500, color: 'text.primary' }}>
            Claims
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0, pb: 2 }}>
        <TableContainer>
          <Table size="small" sx={(theme) => ({ '& .MuiTableCell-root': { borderBottomColor: theme.palette.rowDivider } })}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#fafafa' }}>
                {COLUMNS.map(({ label, width }) => (
                  <TableCell
                    key={label}
                    sx={{
                      fontSize: 12,
                      fontWeight: 500,
                      color: 'text.secondary',
                      letterSpacing: '0.05em',
                      py: 1.5,
                      width,
                    }}
                  >
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {/* An item's provider rows share a number and carry no id; the table is
                  read-only and never reordered, so position is a stable key. */}
              {associatedClaims.map((claim, i) => {
                const isHighlighted = childId !== null && claim.number === childId;
                return (
                  <TableRow
                    key={i}
                    data-highlighted={isHighlighted ? 'true' : undefined}
                    sx={{
                      backgroundColor: isHighlighted ? 'action.selected' : 'background.paper',
                      '&:hover': { backgroundColor: isHighlighted ? 'action.selected' : '#fafafa' },
                    }}
                  >
                    <TableCell sx={{ py: 1.5, fontSize: 13, fontWeight: isHighlighted ? 500 : 400, whiteSpace: 'nowrap' }}>
                      {claim.number ?? '—'}
                    </TableCell>
                    <TableCell sx={{ py: 1.5, fontSize: 13 }}>
                      {claim.providerName ?? '—'}
                    </TableCell>
                    <TableCell sx={{ py: 1.5, fontSize: 13 }}>
                      {claim.claimType}
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <StatusBadge status={claim.status} />
                    </TableCell>
                    <TableCell sx={{ py: 1.5, fontSize: 13, color: 'text.secondary' }}>
                      {formatDateMMDDYYYY(claim.createdAt)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {warning && <Box sx={{ px: 2, py: 2 }}>{warning}</Box>}
      </AccordionDetails>
    </Accordion>
  );
});

export default AssociatedClaimsSection;
